import React, { useEffect } from 'react';
import { Styles } from '@t2c/shared-fe';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import { CustomDialogContainer, CustomDialogTitle } from '../../Common/dialog';
import { FormProvider, RHFTextField } from '@sdf/base';
import { useRenameForm } from '../../hooks';
import Divider from '@mui/material/Divider';

interface Props {
  shouldOpenDialog: boolean;
  handleCloseDialog: () => void;
  initialName: string;
  handleOnRename: (name: string) => void;
  isLoading: boolean;
  dialogTitle: string;
}

export const RenameDialog: React.FC<Props> = ({
  shouldOpenDialog,
  handleCloseDialog,
  initialName,
  handleOnRename,
  isLoading,
  dialogTitle,
}) => {
  const formMethods = useRenameForm({ initialName });
  const { handleSubmit, setValue } = formMethods;

  const handleFormSubmit = async ({ name }: { name: string }) => {
    await handleOnRename(name);
  };

  useEffect(() => {
    if (shouldOpenDialog) {
      setValue('name', initialName);
    }
  }, [shouldOpenDialog]);

  return (
    <CustomDialogContainer open={shouldOpenDialog} maxWidth="sm">
      <FormProvider methods={formMethods} onSubmit={handleSubmit(handleFormSubmit)}>
        <CustomDialogTitle
          sx={styles.title}
          title={dialogTitle}
          onClose={isLoading ? () => {} : handleCloseDialog}
        />
        <DialogContent dividers sx={styles.dialogContent}>
          <Stack rowGap={2}>
            <RHFTextField
              placeholder="Enter new name here..."
              size="medium"
              type="text"
              name="name"
              sx={styles.textField}
            />
          </Stack>

          <Stack flexDirection="row" justifyContent="flex-end" alignItems="center" mt={3} mb={2}>
            <LoadingButton
              type="submit"
              variant="contained"
              sx={styles.saveBtn}
              loading={isLoading}
            >
              Save
            </LoadingButton>
          </Stack>
        </DialogContent>
      </FormProvider>
    </CustomDialogContainer>
  );
};

const styles: Styles = {
  title: {
    color: (theme) => theme.palette.text.primary,
    paddingBottom: '10px',
    paddingTop: '10px',
  },
  dialogContent: {
    padding: '16px',
    backgroundColor: (theme) => theme.palette.background.secondary,
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      border: '1px solid',
      borderRadius: '6px',
      borderColor: (theme) => theme.palette.background.ternary,
      backgroundColor: (theme) => theme.palette.background.secondary,
      '&:hover': {
        borderColor: (theme) => theme.palette.background.activeBorder,
      },
    },
    '& .MuiOutlinedInput-input::placeholder': {
      color: (theme) => theme.palette.text.placeholder,
    },
  },
  saveBtn: {
    color: (theme) => theme.palette.text.primary,
    boxShadow: 'none',
    minWidth: 150,
    '&:hover': {
      boxShadow: (theme) => theme.shadows[1],
    },
  },
};
